import { Component, createMemo, For, JSX, Show } from "solid-js";
import { render } from "solid-js/web";
import { getSeenPlayers } from "../lib/persistentData";

type Player = {
    id: string
    name: string
}

type PlayerSelectorProps = {
    onClose: () => void
    onSelect: (player: Player) => void
    existingFilter?: (player: Player) => boolean
    autoOpen?: boolean
}

const PlayerSelector: Component<PlayerSelectorProps> = (props) => {
    let usernameInput: HTMLInputElement | undefined
    let userIDInput: HTMLInputElement | undefined

    const seen = createMemo(() => {
        const seenPlayers = getSeenPlayers()
        const filtered = props.existingFilter === undefined ? seenPlayers : seenPlayers.filter(props.existingFilter)
        const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name))
        return sorted
    })

    const onFormSubmit: JSX.EventHandler<HTMLFormElement, SubmitEvent> = (e) => {
        e.preventDefault()
        props.onSelect({
            id: userIDInput?.value ?? '',
            name: usernameInput?.value ?? ''
        })
    }

    return (
        <dialog class="modal" open={props.autoOpen === true} onClose={props.onClose}>
            <div class="modal-box">
                <div class="flex flex-row items-center mb-4">
                    <h3 class="text-lg font-bold grow">Player Selector</h3>
                    <form method="dialog">
                        <button class="btn hover:btn-error btn-square" title="Cancel">x</button>
                    </form>
                </div>
                
                <form class="font-semibold space-y-2 max-w-sm mx-auto" onSubmit={onFormSubmit}>
                    <label class="block">
                        <p>Player Name</p>
                        <input type="text" name="username" class="input w-full" ref={usernameInput}/>
                    </label>
                    <label class="block">
                        <p>Steam64 ID</p>
                        <input type="text" name="user-id" class="input w-full" ref={userIDInput}/>
                        <p class="font-light">You can get this from <a href="https://steamid.xyz/" class="underline" target="_blank">https://steamid.xyz/</a></p>
                    </label>
                    <button type="submit" class="btn btn-primary block mx-auto">
                        Select
                    </button>
                </form>

                <Show when={seen().length !== 0}>
                    <div class="divider">or</div>

                    <p class="text-center">Select previously seen player:</p>
                    <ul class="max-w-sm mx-auto space-y-1 max-h-72 overflow-auto">
                        <For each={seen()}>
                            {(seenPlayer) => <li>
                                <button class="btn w-full hover:btn-primary" onClick={() => props.onSelect(seenPlayer)}>
                                    {seenPlayer.name}
                                </button>
                            </li>}
                        </For>
                    </ul>
                </Show>
            </div>
        </dialog>
    )
}

export default PlayerSelector

export async function getPlayer(filter?: (player: Player) => boolean): Promise<Player | undefined> {
    return new Promise((resolve) => {
        const renderRoot = document.createElement('div')
        document.body.appendChild(renderRoot)
        const cleanup = () => {
            dispose()
            document.body.removeChild(renderRoot)
        }
        const onClose = () => {
            cleanup()
            resolve(undefined)
        }
        const onSelect = (player: Player) => {
            cleanup()
            resolve(player)
        }
        const dispose = render(() => <PlayerSelector autoOpen onClose={onClose} onSelect={onSelect} existingFilter={filter}/>, renderRoot)
    })
}